import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Rocket, X, ChevronRight } from "lucide-react";

const MEMBER_ROLES = ["membro", "visitante"];

const ITEMS = [
  { key: "grupo", label: "Confira o seu PGD", desc: "Veja o grupo que você lidera", to: "/grupos" },
  { key: "frequencia", label: "Registre uma frequência", desc: "Marque a presença de um encontro", to: "/frequencia" },
  { key: "participante", label: "Cadastre um participante", desc: "Adicione alguém do seu grupo", to: "/contatos" },
  { key: "meta", label: "Crie uma meta", desc: "Registre um compromisso assumido", to: "/metas", auto: "meta" },
  { key: "formacao", label: "Explore a Formação", desc: "Veja os estudos do mês", to: "/formacao", auto: "formacao" },
];

export function FirstSteps() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [manual, setManual] = useState({});
  const [auto, setAuto] = useState({ meta: false, formacao: false });
  const [dismissed, setDismissed] = useState(true);

  const uid = user?.id || "x";
  const dkey = `fs_dismissed_${uid}`;
  const mkey = `fs_done_${uid}`;

  useEffect(() => {
    if (!user) return;
    setDismissed(localStorage.getItem(dkey) === "1");
    try { setManual(JSON.parse(localStorage.getItem(mkey) || "{}")); } catch { setManual({}); }
    api.get("/commitments").then((r) => setAuto((a) => ({ ...a, meta: (r.data || []).length > 0 }))).catch(() => {});
    api.get("/learn/my-progress").then((r) => {
      const d = r.data || {};
      const any = (d.estudo_done || d.escola_done || d.done || 0) > 0 || (Array.isArray(d.completed) && d.completed.length > 0);
      setAuto((a) => ({ ...a, formacao: !!any }));
    }).catch(() => {});
    // eslint-disable-next-line
  }, [user]);

  const done = useMemo(() => {
    const d = {};
    ITEMS.forEach((it) => { d[it.key] = !!manual[it.key] || (it.auto && auto[it.auto]); });
    return d;
  }, [manual, auto]);

  const count = ITEMS.filter((it) => done[it.key]).length;
  const pct = Math.round((count / ITEMS.length) * 100);

  if (!user || MEMBER_ROLES.includes(user.role) || dismissed) return null;

  const click = (it) => {
    const nm = { ...manual, [it.key]: true };
    setManual(nm);
    localStorage.setItem(mkey, JSON.stringify(nm));
    navigate(it.to);
  };
  const toggle = (it, e) => {
    e.stopPropagation();
    const nm = { ...manual, [it.key]: !done[it.key] };
    setManual(nm);
    localStorage.setItem(mkey, JSON.stringify(nm));
  };
  const hide = () => { localStorage.setItem(dkey, "1"); setDismissed(true); };

  const allDone = count === ITEMS.length;

  return (
    <Card className="p-5 relative overflow-hidden" data-testid="first-steps-card">
      <button onClick={hide} data-testid="first-steps-dismiss" className="absolute top-3 right-3 text-muted-foreground hover:text-foreground">
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Rocket className="h-5 w-5 text-accent" />
        <h3 className="font-display text-base font-semibold">Primeiros passos</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        {allDone ? "Você concluiu todos os passos. Parabéns! 🎉" : "Comece com o pé direito — complete os passos abaixo."}
      </p>
      <div className="mt-3 mb-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>{count} de {ITEMS.length} concluídos</span><span>{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-gradient-to-r from-accent to-primary transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="space-y-1.5">
        {ITEMS.map((it) => (
          <button key={it.key} onClick={() => click(it)} data-testid={`first-step-${it.key}`}
            className="w-full flex items-center gap-3 rounded-xl border border-border px-3 py-2.5 text-left hover:bg-secondary/60 transition-colors">
            <span onClick={(e) => toggle(it, e)} className="shrink-0">
              {done[it.key] ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
            </span>
            <span className="flex-1 min-w-0">
              <span className={`block text-sm font-medium ${done[it.key] ? "line-through text-muted-foreground" : ""}`}>{it.label}</span>
              <span className="block text-xs text-muted-foreground">{it.desc}</span>
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          </button>
        ))}
      </div>
    </Card>
  );
}
